import { ReadonlyVec2 } from 'gl-matrix';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { Tool } from '../Tool';
import { ZoomAction } from '../actions/ZoomAction';

/**
 * {@link BoxZoomTool} lets the user drag a rectangle on the viewport
 * and zooms the dragged view so that the selected region fills it.
 * The zoom applies to the dragged view's own environment.
 *
 * Left-click + drag draws the selection rectangle.
 * Ctrl+Wheel zooming is still available via {@link ZoomAction}.
 */
export class BoxZoomTool extends Tool {
    public readonly toolType = 'boxZoom';

    private readonly _zoomAction = new ZoomAction(ModifierKeys.CTRL);

    /** Canvas-pixel coordinates where the drag started. */
    private _startCanvasCoords: ReadonlyVec2 | null = null;
    /** The view on which the drag is happening. */
    private _activeView: PlaneView | null = null;
    /** 2D rendering context used to draw the selection rectangle. */
    private _ctx: CanvasRenderingContext2D | null = null;

    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button === 0 && view instanceof PlaneView) {
            const canvasCoords = view.clientToCanvas(coordinates);
            this._startCanvasCoords = canvasCoords;
            this._activeView = view;

            const canvas2D = view.get2DCanvas;
            if (canvas2D) {
                // The 2D overlay canvas has no width/height attributes
                // set in JSX (defaults to 300x150). We must sync them
                // with the actual display size before drawing.
                canvas2D.width = canvas2D.offsetWidth;
                canvas2D.height = canvas2D.offsetHeight;
                this._ctx = canvas2D.getContext('2d');
            }

            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        if (this._startCanvasCoords && this._activeView && this._ctx) {
            const currentCanvasCoords =
                this._activeView.clientToCanvas(coordinates);
            this.drawSelectionRect(currentCanvasCoords);

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        _button: number,
        coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        if (this._startCanvasCoords && this._activeView) {
            const endCanvasCoords =
                this._activeView.clientToCanvas(coordinates);

            this.clearOverlay();
            this.zoomToBox(
                this._activeView,
                this._startCanvasCoords,
                endCanvasCoords
            );

            this._startCanvasCoords = null;
            this._activeView = null;
            this._ctx = null;

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        return this._zoomAction.onMouseWheel(
            delta,
            modifierKeys,
            coordinates,
            view
        );
    }

    // ── private helpers ──────────────────────────────────────────

    /**
     * Draws a dashed rectangle on the 2D overlay canvas between
     * the drag start and the current cursor position.
     */
    private drawSelectionRect(current: ReadonlyVec2): void {
        if (!this._ctx || !this._startCanvasCoords || !this._activeView) {
            return;
        }

        const canvas = this._activeView.get2DCanvas;
        if (!canvas) {
            return;
        }

        const w = canvas.width;
        const h = canvas.height;

        // Clear the entire overlay first
        this._ctx.clearRect(0, 0, w, h);

        const x = Math.min(this._startCanvasCoords[0], current[0]);
        const y = Math.min(this._startCanvasCoords[1], current[1]);
        const rw = Math.abs(current[0] - this._startCanvasCoords[0]);
        const rh = Math.abs(current[1] - this._startCanvasCoords[1]);

        this._ctx.save();
        this._ctx.setLineDash([6, 3]);
        this._ctx.strokeStyle = 'rgba(244, 152, 0, 0.9)';
        this._ctx.lineWidth = 1.5;
        this._ctx.strokeRect(x, y, rw, rh);

        // Semi-transparent fill so the selected area is visible
        this._ctx.fillStyle = 'rgba(244, 152, 0, 0.08)';
        this._ctx.fillRect(x, y, rw, rh);
        this._ctx.restore();
    }

    /**
     * Clears the selection rectangle overlay.
     */
    private clearOverlay(): void {
        if (!this._ctx || !this._activeView) {
            return;
        }
        const canvas = this._activeView.get2DCanvas;
        if (canvas) {
            this._ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    /**
     * Zooms the view so that the region between `start` and `end`
     * (both in canvas-pixel coordinates) fills the viewport.
     */
    private zoomToBox(
        view: PlaneView,
        start: ReadonlyVec2,
        end: ReadonlyVec2
    ): void {
        const boxWidth = Math.abs(end[0] - start[0]);
        const boxHeight = Math.abs(end[1] - start[1]);

        // Ignore tiny drags (accidental clicks)
        if (boxWidth < 4 || boxHeight < 4) {
            return;
        }

        // Centre of the dragged box in canvas-pixel coords
        const centerX = (start[0] + end[0]) / 2;
        const centerY = (start[1] + end[1]) / 2;

        // Convert box centre to patient-space coordinates
        const centerPatient = view.patientCoordsFromCanvasCoords(
            centerX,
            centerY
        );

        // Determine the scale factor: how much of the viewport
        // the box occupies, inverted → the zoom multiplier
        const viewSize = view.size;
        const scaleX = viewSize[0] / boxWidth;
        const scaleY = viewSize[1] / boxHeight;
        const zoomMultiplier = Math.min(scaleX, scaleY);

        const panZoom = view.panZoom;
        const newScale = panZoom.scale * zoomMultiplier;

        // Clamp to engine limits (PanZoom allows 0.4 – 10)
        const clampedScale = Math.min(Math.max(newScale, 0.4), 10);

        // Pan the view so the box centre becomes the viewport centre
        view.setViewPosition(centerPatient);
        // Move the slice crosshair to the same point
        view.updatePlaneIntersectionPoint(centerPatient);
        // Apply the new zoom level
        view.updateZoomScale(clampedScale);
        view.resetCurrentScale(clampedScale);

        // Update the view's own PanZoom scale so other UI stays in sync
        panZoom.scale = clampedScale;
    }
}
